import { useEffect, useMemo, useRef } from 'react';
import useSocket, { Options, ReadyState } from 'react-use-websocket';

import { transformIncomingData } from '../api/utils/dataTransform';
import { useAuthQuery } from '../auth/hooks/useAuthQuery';

interface UseWebSocketHook<T> {
  lastJsonMessage: T;
  readyState: ReadyState;
}
interface Params {
  topic: string;
  resource_uuid?: string;
  resource_type?: string;
  trip_id?: number | string;
}

const BASE_URL = window.env.RIDEIQ_WEBSOCKET_URL;

export const useWebSocket = <T>(
  initialParams: Params,
  options: Options = {},
  shouldConnect = true
): UseWebSocketHook<T> => {
  const { userIdentity } = useAuthQuery();

  const didUnmount = useRef(false);

  const defaultOptions = {
    skipAssert: true,
    shouldReconnect: () => {
      return didUnmount.current === false;
    },
    reconnectAttempts: 10,
    reconnectInterval: 3000,
    queryParams: {
      auth_email: userIdentity?.email ? encodeURIComponent(userIdentity.email) : '',
      auth_token: userIdentity?.token || '',
      auth_type: userIdentity?.type || '',
      ...initialParams,
    },
    ...options,
  };

  const { readyState, lastJsonMessage } = useSocket(
    BASE_URL,
    {
      skipAssert: true,
      ...(shouldConnect && defaultOptions),
      ...options,
    },
    shouldConnect
  );

  useEffect(() => {
    return () => {
      didUnmount.current = true;
    };
  }, []);

  return { readyState, lastJsonMessage: useMemo(() => transformIncomingData(lastJsonMessage), [lastJsonMessage]) };
};
